'use client';

import { useState } from 'react';

import { dataLayerPush } from '@libs/analytics';
import { activateOffer } from '@libs/api-proxy-client';
import {
    logSentryError,
    SentryAcpTreatsRewardsErrors as SentryErrors,
    useAlertBanner,
    useLoyaltyContext,
    useOptimizely,
} from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { ensureAuth0AccessToken, evaluateSession, isUserLoggedIn } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';

export function getCardSize(offer?: Offer, isFeatured?: boolean) {
    if (isFeatured) {
        return 'featured';
    }

    return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}

export function useOfferCardActions() {
    const { drawerOpen, getOffers, isActivating, selectedOffer, setDrawerOpen, setSelectedOffer } = useLoyaltyContext();

    const { setAlert } = useAlertBanner();
    const { context } = useOptimizely();

    const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());

    function handleCardClick(offer: Offer) {
        setDrawerOpen(true);
        setSelectedOffer(offer);
        context?.trackEvent('offer_details_modal_click');
        // Reset error so pre-existing error doesn't display inside offer drawer
        setAlert(undefined);
    }

    function handleCloseDrawer() {
        setDrawerOpen(false);
        setSelectedOffer(undefined);

        // Reset error so error from offer drawer doesn't display on offer grid
        setAlert(undefined);
    }

    async function handleActivateOffer(offer?: Offer, gtmSection?: string) {
        if (!offer?.isActivatable || offer.isActivated) {
            return;
        }

        setActivatingOffers(prevOffers => new Set(prevOffers).add(offer.offerId));

        try {
            const sessionStillValid = await evaluateSession({});
            if (!sessionStillValid) {
                return;
            }
            await ensureAuth0AccessToken();

            if (!isUserLoggedIn()) {
                return;
            }
            const response = await activateOffer(offer);

            if (response && 'success' in response && response?.success) {
                setAlert(undefined);
                context?.trackEvent('offer_activations');

                // Update selectedOffer immediately to reflect activated state
                if (selectedOffer && selectedOffer.offerId === offer.offerId) {
                    setSelectedOffer({ ...selectedOffer, isActivated: true });
                }
                // Fetch offers again from API to get updated data after activation
                await getOffers();
            } else {
                throw new Error(`Failed to activate an offer, offerId: ${offer.offerId}`);
            }
        } catch (error) {
            SnackbarUtils.showPredefined('SERVER_ERROR');

            dataLayerPush({
                event: 'error',
                site_section: gtmSection || 'treats rewards',
                error_message: `uh oh! [${offer.shortDescription}] order didn't activate. please try again.`,
            });

            logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
        } finally {
            setActivatingOffers(prevOffers => {
                const updatedSet = new Set(prevOffers);
                updatedSet.delete(offer.offerId);
                return updatedSet;
            });
        }
    }

    function showSpinner(offer?: Offer) {
        return offer ? activatingOffers.has(offer.offerId) : false;
    }

    return {
        drawerOpen,
        handleActivateOffer,
        handleCardClick,
        handleCloseDrawer,
        isActivating,
        selectedOffer,
        setDrawerOpen,
        setSelectedOffer,
        showSpinner,
    };
}
